import { Prerequisite, Bundle } from "../loading/prerequisite-loading-service";
import { LoggingService } from "../loggingSvc";
import { FlightingService } from "../flightingSvc";
const mockDataServiceApi = "https://sdmockdataservice.azure-api.net/";
export interface RequestResponseMapping {
  actionName: string;
  httpRequest: {
    method: string;
    path: string;
    body: string;
  };
  httpResponse: {
    statusCode: number;
    isSuccess: boolean;
    response: string;
    errorMessage: string;
    content: {
      filePath: string;
    };
  };
}

export class MockProvider {
  private _mockOperations: Map<string, string[]> = new Map<string, string[]>();
  private _mockOperationsJsonBundleFiles: string[] = [];
  private mockPath: string;

  // @ngInject
  constructor(private $http: ng.IHttpService, private $q: ng.IQService) {
    this.mockPath = "mockDataConfig";
  }

  /**
   * all defined mock operations are returned.
   */
  get mockOperations(): Map<string, string[]> {
    return this._mockOperations;
  }

  /**
   * loads all mock operations defined in configuration file.
   */
  public loadMockOperations(mockOperationsJsonBundle: Bundle[]): void {
    this._mockOperationsJsonBundleFiles = this.getAllBundleFiles(mockOperationsJsonBundle);
    this.getAllMockOperations().then(() => {
      console.log("Found mock operations from all files");
    });
  }

  /**
   * @param prerequisite the prerequisite containing the bundle of mock data paths
   * This method is called upon completion of the prerequisite loader
   */
  public prerequisiteLoadCallback(prerequisite: Prerequisite) {
    if (prerequisite.mockOperationsJsonBundle) {
      this.loadMockOperations(prerequisite.mockOperationsJsonBundle);
    }

    return this.$q.resolve();
  }

  /**
   * returns mock content from file path.
   * @param filePath
   */
  public getMockContent<T>(filePath: string): ng.IPromise<T> {
    let absoluteFilePath: string = this._mockOperationsJsonBundleFiles.find((file) =>
      file.toLowerCase().includes(filePath.toLowerCase())
    );
    console.log("RelativeFilePath: " + filePath + " AbsoluteFilePath: " + absoluteFilePath);
    return this.$http.get<T>(absoluteFilePath).then(
      (result: any) => {
        console.log("Mock Content : " + angular.toJson(result.data));
        return result.data;
      },
      (error) => {
        return this.$q.reject({ message: "Failed to get mock content from file. " + angular.toJson(error) });
      }
    );
  }

  /**
   * returns mock content from Azure API.
   * @param operationName
   */
  public getMockContentfromMockDataServiceAPI<T>(operationName: string): ng.IPromise<T> {
    return this.$http.get<T>(`${mockDataServiceApi}${operationName}`).then(
      (result: any) => {
        console.log(operationName + ": " + angular.toJson(result.data));
        return result.data;
      },
      (error) => {
        return this.$q.reject({ message: "Failed to get mock content from API. " + angular.toJson(error) });
      }
    );
  }

  /**
   * returns files in bundles.
   * @param bundles
   */
  private getAllBundleFiles(bundles: Bundle[]): string[] {
    return bundles
      .filter((bundle: Bundle) => bundle.files)
      .map((bundle: Bundle) => bundle.files)
      .reduce((flatArray: string[], bundleFiles: string[]) => flatArray.concat(bundleFiles), []);
  }

  /**
   * returns mock operations from mock operation file path.
   */
  public getAllMockOperations(): ng.IPromise<any> {
    return this.$q
      .all(this._mockOperationsJsonBundleFiles.map((operationsFile) => this.$http.get(operationsFile)))
      .then((operationsFileResponses: any[]) => {
        let testOperationsFileResponse: any = operationsFileResponses;
        operationsFileResponses.forEach((response) => {
          if (response.status !== 200) {
            console.log(`Unable to retrieve operations.`);
            return;
          }
          if (response.data && !this._mockOperations.has(response.data.mockName)) {
            this._mockOperations.set(response.data.mockName, response.data.mockOperations);
          }
        });
      });
  }
}

export class MockResponseProvider {
  // @ngInject
  constructor(private $q: ng.IQService, protected loggingService: LoggingService, private mcProvider: MockProvider) {}

  /**
   * This method would return mock response based on mockCofig Path and request config
   * @param url
   * @param mockConfigPath
   * @param requestConfig
   */
  public getResponse(url: string, request: any, mockConfigPath: string, requestConfig: any): ng.IPromise<any> {
    return this.mcProvider
      .getMockContent<RequestResponseMapping[]>(mockConfigPath)
      .then((mockResponses: RequestResponseMapping[]) => {
        let responseBy: any = JSON.parse(JSON.stringify(requestConfig));
        if (responseBy && Object.keys(responseBy)[0]) {
          let key: any = Object.keys(responseBy)[0].toLocaleLowerCase();
          if (key) {
            switch (key) {
              case `method`:
                return this.getResponseByHttpMethod(mockResponses, requestConfig).then(
                  (requestResponseMappings: RequestResponseMapping[]) => {
                    return (
                      (requestResponseMappings &&
                        requestResponseMappings.length &&
                        requestResponseMappings[0].httpResponse &&
                        requestResponseMappings[0].httpResponse.content) ||
                      ""
                    );
                  }
                );
              case `url`:
                return this.getResponseByUrl(mockResponses, requestConfig).then(
                  (requestResponseMappings: RequestResponseMapping[]) => {
                    return (
                      (requestResponseMappings &&
                        requestResponseMappings.length &&
                        requestResponseMappings[0].httpResponse &&
                        requestResponseMappings[0].httpResponse.content) ||
                      ""
                    );
                  }
                );
              case `body`:
                return this.getResponseByBody(mockResponses, requestConfig).then(
                  (requestResponseMappings: RequestResponseMapping[]) => {
                    return (
                      (requestResponseMappings &&
                        requestResponseMappings.length &&
                        requestResponseMappings[0].httpResponse &&
                        requestResponseMappings[0].httpResponse.content) ||
                      ""
                    );
                  }
                );
              default:
                return this.getResponseByActionName(mockResponses, requestConfig).then(
                  (requestResponseMappings: RequestResponseMapping[]) => {
                    return (
                      (requestResponseMappings &&
                        requestResponseMappings.length &&
                        requestResponseMappings[0].httpResponse &&
                        requestResponseMappings[0].httpResponse.content) ||
                      ""
                    );
                  }
                );
            }
          }
        }
      });
  }

  /**
   * returns RequestResponseMapping by action name in request config
   * @param mockResponses
   * @param requestConfig
   */
  public getResponseByActionName(
    mockResponses: RequestResponseMapping[],
    requestConfig: any
  ): ng.IPromise<RequestResponseMapping[]> {
    try {
      return this.$q.when(
        mockResponses.filter((requestResponse) => requestResponse.actionName === requestConfig.operationName)
      );
    } catch (error) {
      return this.$q.reject({ message: "Failed to get response by ActionName " + angular.toJson(error) });
    }
  }

  /**
   * returns RequestResponseMapping by request body in request config
   * @param mockResponses
   * @param requestConfig
   */
  public getResponseByBody(
    mockResponses: RequestResponseMapping[],
    requestConfig: any
  ): ng.IPromise<RequestResponseMapping[]> {
    try {
      return this.$q.when(
        mockResponses.filter(
          (requestResponse) =>
            requestResponse.httpRequest &&
            requestResponse.httpRequest.body &&
            requestResponse.httpRequest.body.toLowerCase().indexOf(requestConfig.body.toLowerCase()) >= 0
        )
      );
    } catch (error) {
      return this.$q.reject({ message: "Failed to get response by Body " + angular.toJson(error) });
    }
  }

  /**
   * returns RequestResponseMapping by request url in request config
   * @param mockResponses
   * @param requestConfig
   */
  public getResponseByUrl(
    mockResponses: RequestResponseMapping[],
    requestConfig: any
  ): ng.IPromise<RequestResponseMapping[]> {
    try {
      return this.$q.when(
        mockResponses.filter(
          (requestResponse) =>
            requestResponse.httpRequest &&
            requestResponse.httpRequest.path &&
            requestResponse.httpRequest.path.indexOf(requestConfig.url) >= 0
        )
      );
    } catch (error) {
      return this.$q.reject({ message: "Failed to get response by URL " + angular.toJson(error) });
    }
  }

  /**
   * returns RequestResponseMapping by HTTPMethod in request config
   * @param mockResponses
   * @param requestConfig
   */
  public getResponseByHttpMethod(
    mockResponses: RequestResponseMapping[],
    requestConfig: any
  ): ng.IPromise<RequestResponseMapping[]> {
    try {
      return this.$q.when(
        mockResponses.filter(
          (requestResponse) =>
            requestResponse.httpRequest &&
            requestResponse.httpRequest.method &&
            requestResponse.httpRequest.method === requestConfig.method
        )
      );
    } catch (error) {
      return this.$q.reject({ message: "Failed to get response by HttpMethod " + angular.toJson(error) });
    }
  }
}

export class MockDataService {
  public useMock: string;
  private isMockDataServiceEnabled = false;
  private isMockDataServiceAPIEnabled = false;

  // @ngInject
  constructor(
    private $q: ng.IQService,
    private $location: ng.ILocationService,
    private flightingService: FlightingService,
    private mcResponseProvider: MockResponseProvider,
    private mcProvider: MockProvider
  ) {
    this.useMock = this.useMock || this.$location.search().useMock;
    this.isMockDataServiceAPIEnabled = this.flightingService.isFlightEnabled("ENABLE_MOCK_DATA_SERVICE_API");
  }

  /**
   * This method would return true if mock is enabled for INT environment ONLY based on flight & action configuration.
   * @param mockPath
   * @param actionName
   */
  public isMockEnabled(mockPath: string, actionName: string): boolean {
    // enable mockdataservice if it is INT/Localhost and flight is enabled.
    if (
      (this.$location.host().indexOf("localhost") >= 0 || this.$location.host().indexOf("servicedesk-int") >= 0) &&
      this.flightingService.isFlightEnabled("ENABLE_MOCK_DATA_SERVICE")
    ) {
      this.isMockDataServiceEnabled = true;
    }

    // check if operation is listed for mocking.
    let operations: any = this.mcProvider.mockOperations;
    if (this.isMockDataServiceEnabled && operations && operations.size > 0 && operations.has(mockPath)) {
      if (operations.get(mockPath).length > 0 && operations.get(mockPath).includes(actionName)) {
        return true;
      }
    }

    return false;
  }

  /**
   * This method would return return mock response.
   * @param url
   * @param request
   * @param actionName
   * @param requestConfig
   */
  public getResponse<T>(url: string, request: any, actionName: string, requestConfig: any): ng.IPromise<T> {
    let configFilePath: string = "config-".concat(actionName.concat(".json"));
    return this.mcResponseProvider.getResponse(url, request, configFilePath, requestConfig).then((content: any) => {
      if (content) {
        if (this.isMockDataServiceAPIEnabled) {
          return this.mcProvider.getMockContentfromMockDataServiceAPI<T>(actionName).then((response: any) => {
            return response;
          });
        }
        return this.mcProvider.getMockContent<T>(content.filePath).then((response: any) => {
          return response;
        });
      } else {
        return this.$q.reject({ message: "No content found for requested action." + configFilePath });
      }
    });
  }
}
